import { create } from "zustand";
import { api, ISearchProduct, ISearchResponse } from "../api/api";

export interface ICondition {
  id: number;
  name: string;
}

export interface SearchStoreState {
  // Поисковый запрос
  searchQuery: string;
  setSearchQuery: (query: string) => void;

  // Фильтр по состоянию товара
  conditions: ICondition[];
  selectedCondition: string | null;
  setSelectedCondition: (condition: string | null) => void;
  loadConditions: () => Promise<void>;

  // Фильтр по размерам
  selectedSizes: string[];
  setSelectedSizes: (sizes: string[]) => void;

  // Сортировка
  sortOrder: "asc" | "desc" | null;
  setSortOrder: (order: "asc" | "desc" | null) => void;

  // Данные продуктов
  products: ISearchProduct[];
  totalCount: number;
  isLoading: boolean;
  hasPreloaded: boolean;

  // Загрузка товаров
  loadProducts: (limit?: number, offset?: number) => Promise<void>;
  setProducts: (products: ISearchProduct[], totalCount: number) => void;
  setLoading: (loading: boolean) => void;

  // Метод для сброса всех фильтров
  clearFilters: () => void;
}

export const useSearchStore = create<SearchStoreState>((set, get) => ({
  // Начальное состояние
  searchQuery: "",
  conditions: [],
  selectedCondition: null,
  sortOrder: null,
  selectedSizes: [],
  products: [],
  totalCount: 0,
  isLoading: false,
  hasPreloaded: false,

  // Установка поискового запроса
  setSearchQuery: (query: string) => set({ searchQuery: query }),

  // Установка выбранного состояния товара
  setSelectedCondition: (condition: string | null) =>
    set({ selectedCondition: condition }),

  // Установка порядка сортировки
  setSortOrder: (order: "asc" | "desc" | null) => set({ sortOrder: order }),

  // Установка выбранных размеров
  setSelectedSizes: (sizes: string[]) => set({ selectedSizes: sizes }),

  // Загрузка списка состояний товара
  loadConditions: async () => {
    try {
      const response = await api.getConditions();
      if (
        response &&
        response.conditions &&
        Array.isArray(response.conditions)
      ) {
        set({ conditions: response.conditions });
      } else if (response && Array.isArray(response)) {
        set({ conditions: response });
      } else {
        set({ conditions: [] });
      }
    } catch (error) {
      console.error("Ошибка при загрузке списка состояний товара:", error);
    }
  },

  // Загрузка товаров с учетом фильтров
  loadProducts: async (limit: number = 16, offset: number = 0) => {
    try {
      set({ isLoading: true });
      const state = get();
      
      // Загружаем conditions, если они еще не загружены
      if (state.conditions.length === 0) {
        await get().loadConditions();
      }
      
      const response = await api.getAllProductsInStock(
        limit,
        offset,
        undefined,
        state.selectedCondition || undefined,
        state.sortOrder || undefined,
        state.selectedSizes.length > 0 ? state.selectedSizes : undefined
      );
      
      set({ 
        products: response.stock || [],
        totalCount: response.total_count || 0,
        isLoading: false,
        hasPreloaded: true
      });
    } catch (error) {
      console.error("Ошибка при загрузке товаров:", error);
      set({ isLoading: false });
    }
  },

  setProducts: (products: ISearchProduct[], totalCount: number) => {
    set({ products, totalCount, hasPreloaded: true });
  },

  setLoading: (loading: boolean) => {
    set({ isLoading: loading });
  },

  // Сброс всех фильтров
  clearFilters: () => {
    set({
      searchQuery: "",
      selectedCondition: null,
      sortOrder: null,
      selectedSizes: [],
    });
  },
}));
